import {
  useModalOldContext,
  WSButton,
  WSButtons,
  WSModalOld,
  WSRadioInputGroup,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RecurrenceScheduleDate,
  RecurrenceScheduleDateStatus,
  RecurringInvoice
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router";
import { ErrorAlert } from "@components/ErrorAlert";
import { recurringInvoicesApi } from "@api";
import { queriesRecurringInvoices } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import { useWSMutation } from "../../../query/helpers";
import { selectorNaturalOccurrenceDate } from "../../selectors/selectorNaturalOccurrenceDate";
import { selectorScheduleWithOccurrence } from "../../selectors/selectorScheduleWithOccurrence";

export const SERIES_INVOICE_ACTIONS_MODAL = "SERIES_INVOICE_ACTIONS_MODAL";

type Action = "edit" | "revert" | "skip" | "renew" | "renewAndEdit";

type ModalProps = {
  parentInvoiceTemplateId: string;
  scheduleDateIndex: number;
};

export const SeriesInvoiceActionsModal: React.FC = () => {
  return (
    <WSModalOld name={SERIES_INVOICE_ACTIONS_MODAL} size="XS">
      {(props: ModalProps) => {
        return <SeriesInvoiceActions {...props} />;
      }}
    </WSModalOld>
  );
};

const SeriesInvoiceActions: React.FC<ModalProps> = props => {
  const queryParentRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId: props.parentInvoiceTemplateId
    });

  const scheduleDate =
    queryParentRecurringInvoice.data?.schedule?.scheduleDates?.[
      props.scheduleDateIndex
    ];

  const overrideId = scheduleDate?.invoiceTemplateId;
  const queryOverrideRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery(
      { recurringInvoiceId: overrideId! },
      { enabled: !!overrideId, retry: false }
    );

  const queryOverrideForGate = !overrideId
    ? { ...queryOverrideRecurringInvoice, isSuccess: true, data: undefined }
    : queryOverrideRecurringInvoice;

  return (
    <WSQueries
      queries={{
        queryParentRecurringInvoice,
        queryOverrideRecurringInvoice: queryOverrideForGate
      }}
    >
      {({
        queryParentRecurringInvoice: { data: parentRecurringInvoice },
        queryOverrideRecurringInvoice: { data: override }
      }) => {
        const resolvedScheduleDate =
          parentRecurringInvoice.schedule?.scheduleDates?.[
            props.scheduleDateIndex
          ];

        if (!resolvedScheduleDate) {
          return (
            <WSText color="garnet">
              This invoice is no longer part of the series.
            </WSText>
          );
        }

        return (
          <SeriesInvoiceActionsForm
            parentRecurringInvoice={parentRecurringInvoice}
            override={override}
            scheduleDate={resolvedScheduleDate}
            scheduleDateIndex={props.scheduleDateIndex}
          />
        );
      }}
    </WSQueries>
  );
};

type FormProps = {
  parentRecurringInvoice: RecurringInvoice;
  override?: RecurringInvoice;
  scheduleDate: RecurrenceScheduleDate;
  scheduleDateIndex: number;
};

const SeriesInvoiceActionsForm: React.FC<FormProps> = props => {
  const history = useHistory();
  const location = useLocation();
  const { closeModal } = useModalOldContext();

  const isSkipped =
    (props.scheduleDate.status === RecurrenceScheduleDateStatus.MODIFIED &&
      props.override?.schedule?.scheduleDates?.[0]?.status ===
        RecurrenceScheduleDateStatus.SKIPPED) ||
    props.scheduleDate.status === RecurrenceScheduleDateStatus.SKIPPED;

  const [action, setAction] = useState<Action>(isSkipped ? "renew" : "edit");

  const edit = (scheduleDateIndex: number) => {
    history.push({
      pathname: location.pathname + "/" + scheduleDateIndex,
      state: {
        backPath: location.pathname
      },
      search: location.search
    });
    closeModal(SERIES_INVOICE_ACTIONS_MODAL);
  };

  const setOccurrenceStatus = async (
    status: RecurrenceScheduleDateStatus
  ): Promise<void> => {
    if (props.override?.id) {
      await recurringInvoicesApi.updateRecurringInvoice({
        recurringInvoiceId: props.override.id,
        recurringInvoiceUpdate: {
          schedule: selectorScheduleWithOccurrence({
            schedule: props.override.schedule,
            index: 0,
            occurrence: { status }
          })
        }
      });

      return;
    }

    await recurringInvoicesApi.updateRecurringInvoice({
      recurringInvoiceId: props.parentRecurringInvoice.id ?? "",
      recurringInvoiceUpdate: {
        schedule: selectorScheduleWithOccurrence({
          schedule: props.parentRecurringInvoice.schedule,
          index: props.scheduleDateIndex,
          occurrence: { status }
        })
      }
    });
  };

  const [submit, submitMeta] = useWSMutation(
    async () => {
      switch (action) {
        case "skip":
          await setOccurrenceStatus(RecurrenceScheduleDateStatus.SKIPPED);
          break;
        case "revert": {
          // The override write moved the parent's own occurrence date, so
          // tearing down the child series is not enough — put the occurrence
          // back on its cadence date (PLA-3585).
          const naturalDate = selectorNaturalOccurrenceDate({
            schedule: props.parentRecurringInvoice.schedule,
            index: props.scheduleDateIndex
          });

          await recurringInvoicesApi.updateRecurringInvoice({
            recurringInvoiceId: props.parentRecurringInvoice.id ?? "",
            recurringInvoiceUpdate: {
              schedule: selectorScheduleWithOccurrence({
                schedule: props.parentRecurringInvoice.schedule,
                index: props.scheduleDateIndex,
                occurrence: {
                  status: RecurrenceScheduleDateStatus.PENDING,
                  invoiceTemplateId: undefined,
                  ...(naturalDate ? { date: naturalDate } : {})
                }
              })
            }
          });

          if (props.override?.id) {
            await recurringInvoicesApi.cancelRecurringInvoice({
              recurringInvoiceId: props.override.id
            });
          }

          break;
        }
        case "renew":
          await setOccurrenceStatus(RecurrenceScheduleDateStatus.PENDING);
          break;
        case "renewAndEdit":
          await setOccurrenceStatus(RecurrenceScheduleDateStatus.PENDING);
          edit(props.scheduleDateIndex);
          break;
        case "edit":
        default:
          edit(props.scheduleDateIndex);
      }

      closeModal(SERIES_INVOICE_ACTIONS_MODAL);
    },
    {
      dependencies: [
        queriesRecurringInvoices.getRecurringInvoice.queryKeyBase,
        queriesRecurringInvoices.listRecurringInvoices.queryKeyBase
      ]
    }
  );

  return (
    <>
      <WSText.Heading5 mb="M">Invoice actions</WSText.Heading5>
      <WSRadioInputGroup
        mb="2XL"
        name="action"
        value={action}
        options={
          isSkipped
            ? [
                {
                  value: "renew",
                  label: "Renew"
                },
                {
                  value: "renewAndEdit",
                  label: "Renew and edit"
                }
              ]
            : props.scheduleDate.invoiceTemplateId
              ? [
                  {
                    value: "edit",
                    label: "Edit invoice"
                  },
                  {
                    value: "revert",
                    label: "Revert changes"
                  },
                  {
                    value: "skip",
                    label: "Skip this instance"
                  }
                ]
              : [
                  {
                    value: "edit",
                    label: "Edit invoice"
                  },
                  {
                    value: "skip",
                    label: "Skip this instance"
                  }
                ]
        }
        onChange={(event: any) => {
          setAction(event.target.value);
        }}
      />

      <ErrorAlert
        my="XL"
        context="UpdateSeriesInvoice"
        error={submitMeta.error}
      />

      <WSButtons format="modal">
        <WSButton
          loading={submitMeta.isLoading}
          onClick={() => {
            submit();
          }}
        >
          Continue
        </WSButton>
        <WSButton.Secondary
          onClick={() => {
            closeModal(SERIES_INVOICE_ACTIONS_MODAL);
          }}
        >
          Cancel
        </WSButton.Secondary>
      </WSButtons>
    </>
  );
};
